// Redaktionell sanningskälla (E-E-A-T).
// Byt namn/roll/LinkedIn här — bylines, Person-schema och /redaktionell-policy
// läser alla från den här filen. Ingen annan fil ska hårdkoda författarnamn.

export interface EditorialPerson {
  /** Publikt namn i bylinen. */
  name: string;
  /** Rolltitel som visas under namnet och i Person-schema (jobTitle). */
  role: string;
  /** Kort meritmening — varför den här personen kan ämnet. */
  credentials?: string;
  /** Porträtt (public/ eller absolut URL). Tomt = initial-avatar. */
  image?: string;
  /** Verifierad profil. Lämna tom tills den är bekräftad — hellre inget än fel. */
  linkedin?: string;
  /** Ämnesexpertis — speglas som knowsAbout i Person-schema (E-E-A-T/GEO). */
  knowsAbout?: string[];
}

/**
 * Nyckeln används som `authorKey` i AuthorByline.
 * TODO (kräver ert beslut): ersätt `default` med en namngiven person
 * (t.ex. `anna`) och sätt `linkedin` + `image`. Behåll `default` som fallback.
 */
export const EDITORIAL_PEOPLE: Record<string, EditorialPerson> = {
  mattias: {
    name: "Mattias Björk",
    role: "VD och ansvarig utgivare",
    credentials:
      "Driver Fenny och Norrhavet Group. Sätter briefen i produktionerna och ansvarar för att prisdata, ledtider och branschstudier stämmer med vad vi faktiskt levererar.",
    linkedin: "https://www.linkedin.com/in/mattias-bj%C3%B6rk/",
    knowsAbout: [
      "Filmproduktion",
      "Reklamfilm",
      "Prissättning av filmproduktion",
      "Videostrategi B2B",
      "Employer branding-film",
      "Produktionsledning",
    ],
  },
  default: {
    name: "Fenny-redaktionen",
    role: "Producenter & strateger",
    credentials:
      "Teamet som producerar Fennys filmer — prissättning, ledtider och format bygger på våra egna produktioner.",
    knowsAbout: [
      "Filmproduktion",
      "Reklamfilm",
      "Företagsfilm",
      "Employer branding-film",
      "Prissättning av filmproduktion",
      "Videostrategi B2B",
    ],
  },
};

/** Hur ofta innehåll granskas om, per innehållstyp. Visas på policysidan. */
export const REVIEW_CADENCE: { type: string; interval: string }[] = [
  { type: "Prisguider och priser", interval: "Var tredje månad" },
  { type: "Frågesidor (/fragor)", interval: "Var sjätte månad" },
  { type: "Jämförelser (/jamfor)", interval: "Var sjätte månad" },
  { type: "Kunskapsartiklar", interval: "Årligen eller vid faktaändring" },
];

/** Principer — speglas i policysidans synliga text. */
export const EDITORIAL_PRINCIPLES: { title: string; body: string }[] = [
  {
    title: "Egna data först",
    body: "Prisspann, ledtider och format-benchmarks kommer från Fennys egna produktioner. Där vi använder extern statistik anger vi källa och år.",
  },
  {
    title: "Inga påhittade siffror",
    body: "Vi publicerar hellre ett intervall eller ingenting alls än en exakt siffra vi inte kan belägga. Inga fejkade omdömen, betyg eller kundlöften.",
  },
  {
    title: "Synligt granskningsdatum",
    body: "Varje sida med fakta visar när den senast granskades. Datumet kommer från innehållets verkliga ändringshistorik — inte från när sidan renderades.",
  },
  {
    title: "Rättelser",
    body: "Hittar du ett fel? Mejla oss så rättar vi och uppdaterar granskningsdatumet. Väsentliga rättelser noteras i texten.",
  },
  {
    title: "AI som verktyg, inte som avsändare",
    body: "Vi kan använda AI för research och utkast. Allt publicerat innehåll läses, faktagranskas och godkänns av en människa i teamet.",
  },
];

export const EDITORIAL_POLICY_PATH = "/redaktionell-policy";

/** Redaktionens hubbsida. */
export const EDITORIAL_HUB_PATH = "/redaktionen";

/**
 * Varje redaktionsnyckel får en egen författarsida. Sidan är den
 * verifierbara noden i E-E-A-T-kedjan: biografi, expertisområden och
 * sameAs till LinkedIn — det som Google och AI-motorer kan koppla ihop
 * med bylinen på varje artikel.
 */
export const authorPath = (key: string) => `${EDITORIAL_HUB_PATH}/${key}`;

/** Stabilt schema-@id för en person, så bylines och författarsidor pekar på samma nod. */
export const authorId = (key: string) => `https://fenny.se${authorPath(key)}#person`;

export const EDITORIAL_KEYS = Object.keys(EDITORIAL_PEOPLE);

export const getEditorialPerson = (key: string): EditorialPerson | undefined =>
  EDITORIAL_PEOPLE[key];
